import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, Lightbulb, Shield, Users } from 'lucide-react';

const founderImage = 'https://res.cloudinary.com/dnhlbls94/image/upload/v1771407822/Gemini_Generated_Image_e2mai2e2mai2e2ma_xlbixk.jpg';

const team = [
  {
    name: 'Discover',
    role: 'Strategic Alignment',
    accentA: '#00d4aa',
    accentB: '#06b6d4',
    visual: 'discover',
    bio: 'We begin by deeply understanding your business model, competitive landscape, operational constraints, and growth objectives. Every technical decision is aligned with measurable business impact.',
  },
  {
    name: 'Design',
    role: 'Architecture & Experience',
    accentA: '#22d3ee',
    accentB: '#818cf8',
    visual: 'design',
    bio: 'We architect scalable system foundations and craft intuitive user experiences. Every layer is intentionally designed for clarity, performance, and long-term adaptability.',
  },
  {
    name: 'Engineer',
    role: 'Intelligent Implementation',
    accentA: '#6366f1',
    accentB: '#22d3ee',
    visual: 'engineer',
    bio: 'We build production-grade systems using modern, secure, and maintainable technology stacks, ensuring reliability, scalability, and performance from day one.',
  },
  {
    name: 'Scale',
    role: 'Optimization & Evolution',
    accentA: '#a855f7',
    accentB: '#6366f1',
    visual: 'scale',
    bio: 'We continuously refine and optimize your systems. Through monitoring, automation, and strategic iteration, your technology compounds in value as your business grows.',
  },
];

const createTechPattern = (visual, accentA, accentB) =>
  `url("data:image/svg+xml;utf8,${encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 700'>
      <defs>
        <linearGradient id='gradA' x1='0' y1='0' x2='1' y2='1'>
          <stop offset='0%' stop-color='${accentA}' stop-opacity='0.44'/>
          <stop offset='100%' stop-color='${accentB}' stop-opacity='0.14'/>
        </linearGradient>
        <linearGradient id='gradB' x1='0' y1='1' x2='1' y2='0'>
          <stop offset='0%' stop-color='${accentB}' stop-opacity='0.22'/>
          <stop offset='100%' stop-color='${accentA}' stop-opacity='0.36'/>
        </linearGradient>
      </defs>
      ${
        visual === 'discover'
          ? `
      <g fill='none' stroke='url(%23gradA)' stroke-width='1.8'>
        <path d='M90 480 L250 360 L420 440 L620 300 L860 360 L1080 220'/>
        <path d='M130 560 L310 490 L510 560 L730 420 L930 500'/>
      </g>
      <g fill='${accentA}' opacity='0.9'>
        <circle cx='250' cy='360' r='6'/><circle cx='420' cy='440' r='5'/><circle cx='620' cy='300' r='6'/><circle cx='860' cy='360' r='5'/>
      </g>
      <g fill='${accentB}' opacity='0.75'>
        <circle cx='90' cy='480' r='4'/><circle cx='1080' cy='220' r='4'/><circle cx='310' cy='490' r='4'/><circle cx='730' cy='420' r='4'/>
      </g>
      <g stroke='rgba(148,163,184,0.2)' stroke-width='1'>
        <path d='M250 360 L250 550'/><path d='M620 300 L620 550'/><path d='M860 360 L860 550'/>
      </g>
      `
          : visual === 'design'
          ? `
      <g stroke='rgba(148,163,184,0.28)' stroke-width='1.2' fill='none'>
        <path d='M110 160 L380 160 L520 280 L250 280 Z'/>
        <path d='M480 120 L760 120 L900 250 L620 250 Z'/>
        <path d='M220 340 L500 340 L650 500 L370 500 Z'/>
      </g>
      <g stroke='url(%23gradB)' stroke-width='1.5' fill='none'>
        <path d='M250 280 L250 460 M380 160 L380 340 M620 250 L620 430 M760 120 L760 300'/>
        <path d='M100 600 L1140 600'/>
      </g>
      <g fill='${accentB}' opacity='0.55'>
        <rect x='270' y='195' width='85' height='8' rx='4'/>
        <rect x='640' y='155' width='85' height='8' rx='4'/>
        <rect x='390' y='380' width='95' height='8' rx='4'/>
      </g>
      `
          : visual === 'engineer'
          ? `
      <g fill='rgba(148,163,184,0.14)' stroke='rgba(148,163,184,0.22)' stroke-width='1.2'>
        <rect x='120' y='150' width='220' height='150' rx='18'/>
        <rect x='360' y='220' width='240' height='165' rx='22'/>
        <rect x='640' y='150' width='270' height='180' rx='24'/>
      </g>
      <g fill='none' stroke='url(%23gradA)' stroke-width='2'>
        <path d='M40 520 L220 520 L220 440 L420 440 L420 340 L680 340 L680 260 L1110 260'/>
        <path d='M80 590 L310 590 L310 470 L560 470 L560 390 L850 390'/>
      </g>
      <g fill='${accentA}' opacity='0.85'>
        <circle cx='220' cy='440' r='5'/><circle cx='420' cy='340' r='5'/><circle cx='680' cy='260' r='5'/><circle cx='560' cy='390' r='4'/>
      </g>
      <g fill='${accentB}' opacity='0.72'>
        <circle cx='850' cy='390' r='4'/><circle cx='1110' cy='260' r='4'/>
      </g>
      `
          : `
      <g fill='none' stroke='url(%23gradB)' stroke-width='1.8'>
        <path d='M140 570 C260 520 380 500 560 430 S920 300 1080 210'/>
        <path d='M220 610 C360 550 540 520 740 420'/>
      </g>
      <g fill='none' stroke='rgba(148,163,184,0.2)' stroke-width='1'>
        <circle cx='230' cy='520' r='44'/><circle cx='230' cy='520' r='72'/><circle cx='230' cy='520' r='104'/>
      </g>
      <g fill='${accentA}' opacity='0.82'>
        <circle cx='560' cy='430' r='6'/><circle cx='760' cy='350' r='6'/><circle cx='920' cy='300' r='7'/><circle cx='1080' cy='210' r='8'/>
      </g>
      <g fill='${accentB}' opacity='0.6'>
        <circle cx='680' cy='390' r='3'/><circle cx='845' cy='330' r='3'/><circle cx='980' cy='258' r='3'/>
      </g>
      `
      }
    </svg>`
  )}")`;

const values = [
  {
    icon: <Target size={24} />,
    title: 'Outcome-Driven',
    desc: 'We measure success by the business results our software delivers, not just the code we ship.',
    color: '#00d4aa',
  },
  {
    icon: <Lightbulb size={24} />,
    title: 'Intellectually Honest',
    desc: 'We recommend the right solution, not the most complex one. Simplicity is sophistication.',
    color: '#06b6d4',
  },
  {
    icon: <Shield size={24} />,
    title: 'Built to Last',
    desc: 'We engineer for scale, security, and maintainability  code that’s still solid five years from now.',
    color: '#6366f1',
  },
  {
    icon: <Users size={24} />,
    title: 'True Partnership',
    desc: 'We work as embedded partners, not vendors. Your success is our success, every sprint.',
    color: '#8b5cf6',
  },
];

const About = () => (
  <main data-testid="about-page">
    {/* Hero */}
    <section
      style={{
        minHeight: '55vh',
        background: '#0a0f1e',
        display: 'flex',
        alignItems: 'flex-end',
        paddingTop: 'clamp(96px, 14vw, 120px)',
        paddingBottom: 'clamp(56px, 8vw, 80px)',
        borderBottom: '1px solid rgba(255,255,255,0.05)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: '0',
          right: '-100px',
          width: 'min(60vw, 600px)',
          height: 'min(60vw, 600px)',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(0,212,170,0.05) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />
      <div className="max-w-7xl mx-auto px-6 md:px-12 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="flex flex-col justify-center h-full text-center">
            <motion.span
              className="section-label"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              // About InsightsNode
            </motion.span>
            <motion.h1
              style={{
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 800,
                fontSize: 'clamp(2.5rem, 6vw, 5rem)',
                letterSpacing: '-0.04em',
                color: '#fff',
                lineHeight: 1.0,
              }}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.15 }}
            >
              The Architects
              <br />
              <span className="text-gradient-teal">of Intelligence.</span>
            </motion.h1>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.35 }}
          >
            <p
              style={{
                color: '#94a3b8',
                fontFamily: 'Inter',
                fontSize: '1.05rem',
                lineHeight: 1.85,
              }}
            >
              InsightsNode is a technology agency that designs and builds intelligent digital systems for
              businesses looking to grow with clarity and confidence. We partner with founders, CTOs,
              agencies, and enterprise teams to transform ideas into scalable, production-ready software.
              From strategy and IT consulting to web and mobile applications, backend systems, APIs, cloud
              infrastructure, DevOps, data and AI solutions, quality assurance, system integrations, and
              ongoing managed operations, we handle the entire software lifecycle end to end.
            </p>
            <p
              style={{
                color: '#64748b',
                fontFamily: 'Inter',
                fontSize: '0.95rem',
                lineHeight: 1.8,
                marginTop: '16px',
              }}
            >
              Founded in Kolkata and serving clients globally, our approach combines strong engineering
              standards with practical execution and a clear focus on measurable business outcomes. We
              believe technology should be reliable, scalable, and human enabling organizations to take on
              bigger opportunities without increasing complexity or overhead.
            </p>
          </motion.div>
        </div>
      </div>
    </section>

    {/* Mission */}
    <section style={{ padding: 'clamp(80px, 10vw, 120px) 0', background: '#0a0f1e' }}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true, margin: '-80px' }}
          >
            <span className="section-label">// Our Mission</span>
            <h2
              style={{
                fontFamily: 'Manrope',
                fontWeight: 800,
                fontSize: 'clamp(1.8rem, 3.5vw, 3rem)',
                letterSpacing: '-0.03em',
                color: '#fff',
                lineHeight: 1.1,
                marginBottom: '24px',
              }}
            >
              Engineering Systems That
              <span className="text-gradient-teal"> Create Value.</span>
            </h2>
            <p
              style={{
                color: '#94a3b8',
                fontFamily: 'Inter',
                fontSize: '0.95rem',
                lineHeight: 1.85,
                marginBottom: '20px',
              }}
            >
              Our mission is to democratize access to world-class software engineering. Too many businesses
              are held back by outdated systems or expensive, unreliable vendors. We’re changing that.
            </p>
            <p
              style={{
                color: '#64748b',
                fontFamily: 'Inter',
                fontSize: '0.9rem',
                lineHeight: 1.8,
              }}
            >
              From Kolkata to the global stage, InsightsNode works with businesses of all sizes to build
              the technological foundation for sustainable growth. Every engagement begins with a genuine
              understanding of your business goals  not a predefined template.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true, margin: '-80px' }}
          >
            <div
              className="glass-card"
              style={{
                padding: 'clamp(20px, 4vw, 40px)',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '3px',
                  background: 'linear-gradient(90deg, #00d4aa, #06b6d4)',
                }}
              />
              <blockquote
                style={{
                  fontFamily: 'Manrope',
                  fontWeight: 700,
                  fontSize: '1.4rem',
                  color: '#fff',
                  lineHeight: 1.4,
                  letterSpacing: '-0.02em',
                  marginBottom: '24px',
                }}
              >
                &ldquo;We don’t just build software. We engineer the systems that power tomorrow’s most
                ambitious businesses.&rdquo;
              </blockquote>
              <div className="flex items-center gap-4">
                <div
                  style={{
                    width: '72px',
                    height: '72px',
                    borderRadius: '50%',
                    border: '2px solid rgba(0,212,170,0.45)',
                    background: 'radial-gradient(circle at 30% 25%, rgba(0,212,170,0.35), rgba(6,182,212,0.1) 45%, rgba(10,15,30,0.9) 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: '0 8px 24px rgba(0,212,170,0.18)',
                    color: '#00d4aa',
                    fontFamily: 'Manrope',
                    fontWeight: 800,
                    fontSize: '1.1rem',
                  }}
                >
                  <img
                    src={founderImage}
                    alt="Sayantan Sen"
                    style={{
                      width: '100%',
                      height: '100%',
                      position: 'absolute',
                      inset: 0,
                      objectFit: 'cover',
                      objectPosition: 'center 44%',
                      zIndex: 2,
                    }}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                  <span style={{ position: 'relative', zIndex: 1 }}>S</span>
                </div>
                <div>
                  <p style={{ fontFamily: 'Manrope', fontWeight: 700, fontSize: '0.9rem', color: '#fff' }}>Sayantan Sen</p>
                  <p style={{ fontFamily: 'Inter', fontSize: '0.8rem', color: '#64748b' }}>Founder, InsightsNode</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>

    {/* Values */}
    <section style={{ padding: 'clamp(80px, 10vw, 120px) 0', background: '#0d1b35' }}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true, margin: '-80px' }}
          style={{ marginBottom: '72px' }}
        >
          <span className="section-label">// Our Values</span>
          <h2
            style={{
              fontFamily: 'Manrope',
              fontWeight: 800,
              fontSize: 'clamp(1.8rem, 3.5vw, 3rem)',
              letterSpacing: '-0.03em',
              color: '#fff',
              lineHeight: 1.1,
            }}
          >
            How We Think.
          </h2>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {values.map((v, i) => (
            <motion.div
              key={v.title}
              className="glass-card card-glow"
              style={{ padding: '36px', display: 'flex', gap: '20px', alignItems: 'flex-start' }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              viewport={{ once: true, margin: '-60px' }}
            >
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  background: `rgba(${v.color === '#00d4aa' ? '0,212,170' : v.color === '#06b6d4' ? '6,182,212' : v.color === '#6366f1' ? '99,102,241' : '139,92,246'}, 0.1)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  color: v.color,
                }}
              >
                {v.icon}
              </div>
              <div>
                <h3 style={{ fontFamily: 'Manrope', fontWeight: 700, fontSize: '1.1rem', color: '#fff', marginBottom: '8px' }}>
                  {v.title}
                </h3>
                <p style={{ color: '#64748b', fontFamily: 'Inter', fontSize: '0.9rem', lineHeight: 1.7 }}>
                  {v.desc}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* Execution Framework (replacing Team) */}
    <section style={{ padding: 'clamp(80px, 10vw, 120px) 0', background: '#0a0f1e' }}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true, margin: '-80px' }}
          style={{ marginBottom: '72px' }}
        >
          <span className="section-label">// Our Approach</span>
          <h2
            style={{
              fontFamily: 'Manrope',
              fontWeight: 800,
              fontSize: 'clamp(1.8rem, 3.5vw, 3rem)',
              letterSpacing: '-0.03em',
              color: '#fff',
              lineHeight: 1.1,
            }}
          >
            Our Execution
            <span className="text-gradient-teal"> Framework.</span>
          </h2>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {team.map((member, i) => (
            <motion.div
              key={member.name}
              className="glass-card card-glow"
              style={{ overflow: 'hidden' }}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              whileHover={{
                y: -8,
                transition: { duration: 0.22, ease: 'easeOut' },
              }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              viewport={{ once: true, margin: '-60px' }}
              data-testid={`team-member-${i}`}
            >
              <motion.div
                style={{
                  height: '200px',
                  position: 'relative',
                  overflow: 'hidden',
                  backgroundImage: `
                    radial-gradient(circle at 18% 22%, ${member.accentA}4D 0%, transparent 34%),
                    radial-gradient(circle at 82% 24%, ${member.accentB}3A 0%, transparent 36%),
                    radial-gradient(circle at 70% 78%, ${member.accentB}2A 0%, transparent 44%),
                    linear-gradient(145deg, rgba(16, 32, 72, 0.76) 0%, rgba(7, 14, 34, 0.94) 100%),
                    repeating-linear-gradient(0deg, rgba(148,163,184,0.08) 0px, rgba(148,163,184,0.08) 1px, transparent 1px, transparent 28px),
                    repeating-linear-gradient(90deg, rgba(148,163,184,0.07) 0px, rgba(148,163,184,0.07) 1px, transparent 1px, transparent 28px),
                    ${createTechPattern(member.visual, member.accentA, member.accentB)}
                  `,
                  backgroundPosition: 'center',
                  backgroundBlendMode: 'screen, screen, screen, normal, overlay, overlay, normal',
                  borderBottom: '1px solid rgba(255,255,255,0.05)',
                }}
              >
                <motion.div
                  style={{
                    position: 'absolute',
                    inset: '-15%',
                    background: `radial-gradient(circle, ${member.accentA}2A 0%, transparent 58%)`,
                    mixBlendMode: 'screen',
                  }}
                  animate={{ x: [-10, 12, -10], y: [6, -6, 6], opacity: [0.35, 0.55, 0.35] }}
                  transition={{ duration: 8 + i, repeat: Infinity, ease: 'easeInOut' }}
                />
              </motion.div>
              <div style={{ padding: '24px' }}>
                <h3 style={{ fontFamily: 'Manrope', fontWeight: 700, fontSize: '1rem', color: '#fff', marginBottom: '4px' }}>
                  {member.name}
                </h3>
                <p style={{ fontFamily: 'JetBrains Mono', fontSize: '0.7rem', color: member.accentA, letterSpacing: '0.08em', marginBottom: '10px' }}>
                  {member.role}
                </p>
                <p style={{ color: '#64748b', fontFamily: 'Inter', fontSize: '0.82rem', lineHeight: 1.65 }}>
                  {member.bio}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
        <p
          style={{
            textAlign: 'center',
            color: '#64748b',
            marginTop: '60px',
            fontFamily: 'Inter',
            fontSize: '0.9rem',
          }}
        >
          Deeper focus. Stronger systems.
        </p>
      </div>
    </section>

    {/* CTA */}
    <section style={{ padding: 'clamp(72px, 9vw, 100px) 0', background: '#0d1b35', textAlign: 'center' }}>
      <div className="max-w-3xl mx-auto px-6">
        <span className="section-label" style={{ display: 'block', textAlign: 'center', marginBottom: '20px' }}>// Join Our Journey</span>
        <h2
          style={{
            fontFamily: 'Manrope',
            fontWeight: 800,
            fontSize: 'clamp(1.8rem, 4vw, 3rem)',
            color: '#fff',
            letterSpacing: '-0.03em',
            lineHeight: 1.1,
            marginBottom: '32px',
          }}
        >
          Let’s build something
          <span className="text-gradient-teal"> extraordinary.</span>
        </h2>
        <Link to="/contact" className="btn-primary" style={{ fontSize: '1rem', padding: '15px 36px' }} data-testid="about-cta">
          Get in Touch <ArrowRight size={16} />
        </Link>
      </div>
    </section>
  </main>
);

export default About;
